/**
 * RuleEditorPage.jsx
 *
 * Rule editor for HCC pipeline rules — two tabs:
 *   Tab 1: NER Code Corrections   (ner_code_corrections.yaml)
 *   Tab 2: Corroboration Rules    (corroboration_rules.yaml
 *                                  corroboration_outcomes.json
 *                                  corroboration_med_classes.json)
 *
 * Files are fetched from /public/rules/*.
 * Save button is a UI placeholder — wire to FastAPI PUT /rules/{file} later.
 *
 * Dependencies: react, antd, @ant-design/icons, js-yaml
 *   npm install js-yaml
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Button, Tag, Input, Select,
  Badge, Spin, Empty, Tooltip, InputNumber,
  Table, message,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SaveOutlined,
  MedicineBoxOutlined, CheckCircleOutlined,
  StopOutlined, SearchOutlined, WarningOutlined,
} from '@ant-design/icons';
import yaml from 'js-yaml';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ─── Design tokens (same palette as HCCChartChasingPage) ───────────────────
const C = {
  brand:        '#2a4a7c',
  brandDark:    '#162349',
  brandLight:   '#EDE9FE',
  border:       '#E5E7EB',
  gospel:       '#15803D',
  gospelBg:     '#F0FDF4',
  gospelBdr:    '#BBF7D0',
  HEADER_H:     52,
  // condition node colours
  andColor:     '#1D4ED8', andBg: '#EFF6FF', andBdr: '#BFDBFE',
  orColor:      '#D97706', orBg:  '#FFFBEB', orBdr:  '#FDE68A',
  presentColor: '#15803D', presentBg: '#F0FDF4', presentBdr: '#BBF7D0',
  absentColor:  '#B91C1C', absentBg:  '#FFF1F0', absentBdr:  '#FECACA',
  // scope
  encColor:     '#0369A1', encBg: '#E0F2FE',
  chartColor:   '#162349', chartBg: '#F5F3FF',
  // state
  state1Color:  '#15803D', state1Bg: '#F0FDF4',
  state2Color:  '#B91C1C', state2Bg: '#FFF1F0',
};

// ─── Tree helpers ───────────────────────────────────────────────────────────
let _uid = 0;
const genId = () => `n${++_uid}_${Math.random().toString(36).slice(2, 6)}`;

const parseToken = (token = '') => {
  const idx = token.indexOf('::');
  return idx === -1
    ? { prefix: 'PL', value: token }
    : { prefix: token.slice(0, idx), value: token.slice(idx + 2) };
};
const fmtToken = (prefix, value) => `${prefix}::${value}`;

function parseConditions(node) {
  if (!node) return { type: 'group', op: 'AND', id: genId(), children: [] };
  if (node.AND)     return { type: 'group', op: 'AND', id: genId(), children: (node.AND  || []).map(parseConditions) };
  if (node.OR)      return { type: 'group', op: 'OR',  id: genId(), children: (node.OR   || []).map(parseConditions) };
  if ('PRESENT' in node) return { type: 'leaf', op: 'PRESENT', id: genId(), token: node.PRESENT || '' };
  if ('ABSENT'  in node) return { type: 'leaf', op: 'ABSENT',  id: genId(), token: node.ABSENT  || '' };
  return { type: 'leaf', op: 'PRESENT', id: genId(), token: '' };
}

function serializeConditions(node) {
  if (!node) return {};
  if (node.type === 'group') return { [node.op]: (node.children || []).map(serializeConditions) };
  return { [node.op]: node.token };
}

function updateNode(tree, id, fn) {
  if (tree.id === id) return fn(tree);
  if (tree.type === 'group') return { ...tree, children: tree.children.map(c => updateNode(c, id, fn)) };
  return tree;
}
function removeNode(tree, id) {
  if (tree.type === 'group') {
    return { ...tree, children: tree.children.filter(c => c.id !== id).map(c => removeNode(c, id)) };
  }
  return tree;
}
function addChild(tree, groupId, child) {
  if (tree.id === groupId) return { ...tree, children: [...tree.children, child] };
  if (tree.type === 'group') return { ...tree, children: tree.children.map(c => addChild(c, groupId, child)) };
  return tree;
}

// ─── YAML preview highlighter ───────────────────────────────────────────────
const YAMLHighlight = ({ code }) => {
  if (!code) return null;
  const colorLine = (line) => {
    if (/^\s*#/.test(line)) return <span style={{ color: '#9CA3AF' }}>{line}</span>;
    const keyMatch = line.match(/^(\s*-?\s*)([^:]+)(:)(.*)/);
    if (keyMatch) {
      const [, indent, key, colon, rest] = keyMatch;
      const keyColor = /^(id|scope|priority|reason|type|value|code|confidence)$/.test(key.trim())
        ? '#7C3AED' : '#1D4ED8';
      const restColor = /^\s*(true|false)/.test(rest) ? '#D97706'
        : /^\s*\d/.test(rest) ? '#0369A1' : '#15803D';
      return (
        <>
          <span>{indent}</span>
          <span style={{ color: keyColor, fontWeight: 600 }}>{key}</span>
          <span style={{ color: '#6B7280' }}>{colon}</span>
          <span style={{ color: restColor }}>{rest}</span>
        </>
      );
    }
    if (/^\s*-\s+(AND|OR|PRESENT|ABSENT):/.test(line) || /^\s*(AND|OR|PRESENT|ABSENT):/.test(line)) {
      const opColors = { AND: C.andColor, OR: C.orColor, PRESENT: C.presentColor, ABSENT: C.absentColor };
      const matched = line.match(/(AND|OR|PRESENT|ABSENT)/);
      const op = matched?.[1];
      return <span style={{ color: opColors[op] || '#374151', fontWeight: 600 }}>{line}</span>;
    }
    return <span style={{ color: '#374151' }}>{line}</span>;
  };

  return (
    <pre style={{
      fontFamily: 'monospace', fontSize: 13, lineHeight: 1.65,
      background: '#FAFAFA', border: `1px solid ${C.border}`,
      borderRadius: 6, padding: '10px 14px',
      overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      margin: 0, flex: 1,
    }}>
      {code.split('\n').map((line, i) => (
        <div key={i}>{colorLine(line)}</div>
      ))}
    </pre>
  );
};

// ─── Condition tree: single node ────────────────────────────────────────────
const PREFIXES = ['PL', 'PMH', 'MED', 'ANYDX'];

const ConditionNode = ({ node, onUpdate, onRemove, onAddChild, medClasses, depth = 0 }) => {
  const isGroup = node.type === 'group';
  const opMeta = {
    AND:     { color: C.andColor,     bg: C.andBg,     bdr: C.andBdr     },
    OR:      { color: C.orColor,      bg: C.orBg,      bdr: C.orBdr      },
    PRESENT: { color: C.presentColor, bg: C.presentBg, bdr: C.presentBdr },
    ABSENT:  { color: C.absentColor,  bg: C.absentBg,  bdr: C.absentBdr  },
  };
  const m = opMeta[node.op] || {};

  const { prefix, value } = isGroup ? {} : parseToken(node.token);

  return (
    <div style={{
      marginLeft: depth > 0 ? 20 : 0,
      marginTop: 6,
      borderLeft: depth > 0 ? `2px solid ${m.bdr || C.border}` : 'none',
      paddingLeft: depth > 0 ? 10 : 0,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '5px 8px',
        background: m.bg || '#F9FAFB',
        border: `1px solid ${m.bdr || C.border}`,
        borderRadius: 6,
      }}>
        {/* op selector */}
        <Select
          size="small"
          value={node.op}
          onChange={val => onUpdate(node.id, n => ({ ...n, op: val, type: ['AND', 'OR'].includes(val) ? 'group' : 'leaf' }))}
          style={{ width: isGroup ? 80 : 100, fontWeight: 700, fontSize: 13 }}
          popupMatchSelectWidth={false}
        >
          <Option value="AND"><span style={{ color: C.andColor, fontWeight: 700 }}>AND</span></Option>
          <Option value="OR"><span style={{ color: C.orColor, fontWeight: 700 }}>OR</span></Option>
          <Option value="PRESENT"><span style={{ color: C.presentColor, fontWeight: 700 }}>PRESENT</span></Option>
          <Option value="ABSENT"><span style={{ color: C.absentColor, fontWeight: 700 }}>ABSENT</span></Option>
        </Select>

        {/* leaf token inputs */}
        {!isGroup && (
          <>
            <Select
              size="small"
              value={prefix}
              onChange={val => onUpdate(node.id, n => ({ ...n, token: fmtToken(val, parseToken(n.token).value) }))}
              style={{ width: 86, fontSize: 13 }}
              popupMatchSelectWidth={false}
            >
              {PREFIXES.map(p => <Option key={p} value={p}>{p}</Option>)}
            </Select>
            <Text style={{ color: '#9CA3AF', fontSize: 13, fontWeight: 700 }}>::</Text>
            {prefix === 'MED' ? (
              <Select
                size="small"
                showSearch
                value={value || undefined}
                placeholder="Class…"
                onChange={val => onUpdate(node.id, n => ({ ...n, token: fmtToken(parseToken(n.token).prefix, val) }))}
                style={{ minWidth: 160, fontSize: 13 }}
                popupMatchSelectWidth={false}
                filterOption={(input, opt) => (opt?.value || '').toLowerCase().includes(input.toLowerCase())}
              >
                {medClasses.map(cls => <Option key={cls} value={cls}>{cls}</Option>)}
              </Select>
            ) : (
              <Input
                size="small"
                value={value}
                placeholder="e.g. E11 or I48.0"
                onChange={e => onUpdate(node.id, n => ({ ...n, token: fmtToken(parseToken(n.token).prefix, e.target.value) }))}
                style={{ width: 130, fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}
              />
            )}
          </>
        )}

        {/* group label */}
        {isGroup && (
          <Text style={{ color: m.color, fontSize: 12, fontWeight: 600 }}>
            {node.children?.length || 0} condition{node.children?.length !== 1 ? 's' : ''}
          </Text>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {/* add child (group only) */}
          {isGroup && (
            <>
              <Tooltip title="Add condition">
                <Button
                  size="small" icon={<PlusOutlined />}
                  onClick={() => onAddChild(node.id, { type: 'leaf', op: 'PRESENT', id: genId(), token: 'PL::' })}
                  style={{ fontSize: 12, color: C.brand, borderColor: C.brand }}
                />
              </Tooltip>
              <Tooltip title="Add group">
                <Button
                  size="small"
                  onClick={() => onAddChild(node.id, { type: 'group', op: 'AND', id: genId(), children: [] })}
                  style={{ fontSize: 12, color: C.andColor, borderColor: C.andColor }}
                >
                  + Group
                </Button>
              </Tooltip>
            </>
          )}
          {/* remove */}
          {depth > 0 && (
            <Tooltip title="Remove">
              <Button
                size="small" danger icon={<DeleteOutlined />}
                onClick={() => onRemove(node.id)}
                style={{ fontSize: 12 }}
              />
            </Tooltip>
          )}
        </div>
      </div>

      {/* children */}
      {isGroup && node.children?.map(child => (
        <ConditionNode
          key={child.id}
          node={child}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onAddChild={onAddChild}
          medClasses={medClasses}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

// ─── ResizeHandle (same as HCCChartChasingPage) ─────────────────────────────
const ResizeHandle = ({ onDrag }) => (
  <div
    onMouseDown={onDrag}
    style={{ width: 6, flexShrink: 0, cursor: 'col-resize', background: C.border, position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
    onMouseEnter={e => e.currentTarget.style.background = C.brand + '55'}
    onMouseLeave={e => e.currentTarget.style.background = C.border}
  >
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none' }}>
      {[0,1,2,3,4].map(i => <div key={i} style={{ width: 2, height: 2, borderRadius: '50%', background: '#9CA3AF' }} />)}
    </div>
  </div>
);

// ─── Panel label ─────────────────────────────────────────────────────────────
const PanelHeader = ({ label, sub, right }) => (
  <div style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
    <div>
      <Text style={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</Text>
      {sub && <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>{sub}</Text>}
    </div>
    {right}
  </div>
);

// ─── NER Rule List ────────────────────────────────────────────────────────────
const NERRuleList = ({ rules, selectedId, onSelect }) => {
  const grouped = useMemo(() => ({
    encounter: (rules || []).filter(r => r.scope === 'encounter').sort((a, b) => a.priority - b.priority),
    chart:     (rules || []).filter(r => r.scope === 'chart').sort((a, b) => a.priority - b.priority),
  }), [rules]);

  const renderGroup = (label, color, bg, list) => (
    <div key={label}>
      <div style={{ padding: '5px 12px 3px', background: bg, borderBottom: `1px solid ${C.border}` }}>
        <Text style={{ fontSize: 12, fontWeight: 700, color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {label} ({list.length})
        </Text>
      </div>
      {list.map(r => {
        const isActive = r.id === selectedId;
        return (
          <div
            key={r.id}
            onClick={() => onSelect(r)}
            style={{
              padding: '9px 12px', cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              borderLeft: `3px solid ${isActive ? C.brand : 'transparent'}`,
              background: isActive ? C.brandLight : 'transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: isActive ? C.brand : '#E5E7EB',
                color: isActive ? '#fff' : '#6B7280',
                fontFamily: 'monospace',
              }}>
                {r.priority}
              </span>
              <Text style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: '#111827', lineHeight: '15px' }}>
                {r.id}
              </Text>
            </div>
            <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginTop: 2, lineHeight: '14px' }}>
              {(r.reason || '').slice(0, 55)}{(r.reason || '').length > 55 ? '…' : ''}
            </Text>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {renderGroup('Encounter Scope', C.encColor, C.encBg + '88', grouped.encounter)}
      {renderGroup('Chart Scope', C.chartColor, '#D4E7F0', grouped.chart)}
    </div>
  );
};

// ─── NER Rule Form ────────────────────────────────────────────────────────────
const NERRuleForm = ({ rule, onChange }) => {
  if (!rule) return <div style={{ padding: 40, textAlign: 'center' }}><Text style={{ color: '#9CA3AF', fontSize: 15 }}>Select a rule to edit</Text></div>;

  const field = (label, key, Component, props = {}) => (
    <div style={{ marginBottom: 14 }}>
      <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>{label}</Text>
      <Component
        value={rule[key]}
        onChange={val => onChange({ ...rule, [key]: typeof val === 'string' ? val : (val?.target?.value ?? val) })}
        style={{ fontSize: 14, width: '100%' }}
        {...props}
      />
    </div>
  );

  return (
    <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
      {/* id + scope + priority in a row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 2 }}>
          <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Rule ID</Text>
          <Input value={rule.id} readOnly style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, background: '#F9FAFB' }} />
        </div>
        <div style={{ flex: '0 0 120px' }}>
          <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Scope</Text>
          <Select
            value={rule.scope}
            onChange={val => onChange({ ...rule, scope: val })}
            style={{ width: '100%', fontSize: 13 }}
          >
            <Option value="encounter">
              <Tag color="blue" style={{ fontSize: 12 }}>encounter</Tag>
            </Option>
            <Option value="chart">
              <Tag color="purple" style={{ fontSize: 12 }}>chart</Tag>
            </Option>
          </Select>
        </div>
        <div style={{ flex: '0 0 90px' }}>
          <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Priority</Text>
          <InputNumber
            value={rule.priority}
            min={1} max={999}
            onChange={val => onChange({ ...rule, priority: val })}
            style={{ width: '100%', fontSize: 13 }}
          />
        </div>
      </div>

      {field('Trigger', 'trigger', TextArea, { rows: 6, placeholder: 'When does this rule apply? (plain English — used as LLM screening prompt)' })}
      {field('Correction', 'correction', TextArea, { rows: 7, placeholder: 'What should the LLM do? (plain English — used as LLM resolution prompt)' })}
      {field('Reason', 'reason', Input, { placeholder: 'Short audit label stored in trace', style: { fontSize: 14, fontFamily: 'monospace' } })}
    </div>
  );
};

// ─── Corroboration Rule List ─────────────────────────────────────────────────
const CorrRuleList = ({ rules, outcomes, selectedId, onSelect }) => {
  const sorted = useMemo(() => [...(rules || [])].sort((a, b) => a.priority - b.priority), [rules]);
  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {sorted.map(r => {
        const isActive = r.id === selectedId;
        const isHallucin = r.output?.reason?.startsWith('hallucination_prevention');
        const hasOutcome = !!outcomes?.[r.id];
        const state = isHallucin ? 2 : hasOutcome ? 1 : 3;
        const stateMeta = {
          1: { label: 'S1', color: C.state1Color, bg: C.state1Bg },
          2: { label: 'S2', color: C.absentColor, bg: C.absentBg },
          3: { label: 'S3', color: '#D97706', bg: '#FFFBEB' },
        }[state];

        return (
          <div
            key={r.id}
            onClick={() => onSelect(r)}
            style={{
              padding: '9px 12px', cursor: 'pointer',
              borderBottom: `1px solid ${C.border}`,
              borderLeft: `3px solid ${isActive ? C.brand : 'transparent'}`,
              background: isActive ? C.brandLight : 'transparent',
              transition: 'background 0.1s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: isActive ? C.brand : '#E5E7EB',
                color: isActive ? '#fff' : '#6B7280', fontFamily: 'monospace',
              }}>{r.priority}</span>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                background: stateMeta.bg, color: stateMeta.color,
              }}>{stateMeta.label}</span>
              {hasOutcome && (
                <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: C.state1Color }}>
                  {'-> '}{outcomes[r.id].code}
                </span>
              )}
            </div>
            <Text style={{ fontSize: 13, fontWeight: isActive ? 700 : 400, color: '#111827', display: 'block', marginTop: 3, lineHeight: '15px' }}>
              {r.id}
            </Text>
            <div style={{ display: 'flex', gap: 3, marginTop: 3, flexWrap: 'wrap' }}>
              {(r.tags || []).slice(0, 3).map(t => (
                <span key={t} style={{ fontSize: 11, padding: '0 5px', borderRadius: 9999, background: '#F3F4F6', color: '#6B7280', border: `1px solid ${C.border}` }}>{t}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Corroboration Rule Editor ───────────────────────────────────────────────
const CorrRuleEditor = ({ rule, outcomes, onChange, medClasses }) => {
  // ── ALL hooks must be called unconditionally before any return ──
  const condTree = rule?._condTree ?? null;

  const setCondTree = useCallback((newTree) => {
    if (!rule) return;
    onChange({ ...rule, _condTree: newTree });
  }, [rule, onChange]);

  const handleUpdate   = useCallback((id, fn) => setCondTree(updateNode(condTree, id, fn)),    [condTree, setCondTree]);
  const handleRemove   = useCallback((id)      => setCondTree(removeNode(condTree, id)),        [condTree, setCondTree]);
  const handleAddChild = useCallback((gId, ch) => setCondTree(addChild(condTree, gId, ch)),     [condTree, setCondTree]);

  const yamlPreview = useMemo(() => {
    if (!rule) return '';
    const isHallucinPreview = rule.output?.reason?.startsWith('hallucination_prevention');
    void isHallucinPreview; // used below
    const obj = {
      id: rule.id,
      priority: rule.priority,
      description: rule.description,
      effective_from: rule.effective_from,
      tags: rule.tags,
      conditions: serializeConditions(condTree),
      output: rule.output,
    };
    try { return yaml.dump(obj, { lineWidth: 56, indent: 2 }); }
    catch { return '(error generating YAML)'; }
  }, [rule, condTree]);

  // ── Early return AFTER all hooks ──
  if (!rule) return (
    <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ color: '#9CA3AF', fontSize: 15 }}>Select a rule to edit</Text>
    </div>
  );

  const isHallucin = rule.output?.reason?.startsWith('hallucination_prevention');
  const outcome    = outcomes?.[rule.id];
  const state      = isHallucin ? 2 : outcome ? 1 : 3;

  const stateBadge = (
    <span style={{
      fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: state === 1 ? C.state1Bg : state === 2 ? C.absentBg : '#FFFBEB',
      color: state === 1 ? C.state1Color : state === 2 ? C.absentColor : '#D97706',
      border: `1px solid ${state === 1 ? C.gospelBdr : state === 2 ? '#FECACA' : '#FDE68A'}`,
    }}>
      {state === 1 ? '>> State 1 — Inject' : state === 2 ? 'BLOCK State 2 — Block LLM' : '? State 3 — LLM Fallback'}
    </span>
  );

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ── Left: form + condition builder ── */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', overflowY: 'auto', flex: 1 }}>

          {/* id / priority / state row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 2 }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Rule ID</Text>
              <Input value={rule.id} readOnly style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, background: '#F9FAFB' }} />
            </div>
            <div style={{ flex: '0 0 80px' }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Priority</Text>
              <InputNumber value={rule.priority} min={1} max={999} onChange={val => onChange({ ...rule, priority: val })} style={{ width: '100%', fontSize: 13 }} />
            </div>
            <div style={{ flex: '0 0 140px', paddingTop: 22 }}>{stateBadge}</div>
          </div>

          {/* description */}
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Description</Text>
            <TextArea
              value={rule.description}
              onChange={e => onChange({ ...rule, description: e.target.value })}
              rows={2} style={{ fontSize: 13 }}
            />
          </div>

          {/* tags */}
          <div style={{ marginBottom: 14 }}>
            <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Tags</Text>
            <Select
              mode="tags" style={{ width: '100%', fontSize: 13 }}
              value={rule.tags || []}
              onChange={vals => onChange({ ...rule, tags: vals })}
              tokenSeparators={[',']}
              placeholder="e.g. diabetes, cardiac, hcc_37"
            />
          </div>

          {/* Condition tree */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Conditions</Text>
              <Text style={{ fontSize: 12, color: '#9CA3AF' }}>PL / PMH / MED / ANYDX</Text>
            </div>
            <div style={{
              background: '#FAFAFA', border: `1px solid ${C.border}`,
              borderRadius: 7, padding: '10px 10px 10px 8px',
            }}>
              {condTree ? (
                <ConditionNode
                  node={condTree}
                  onUpdate={handleUpdate}
                  onRemove={handleRemove}
                  onAddChild={handleAddChild}
                  medClasses={medClasses}
                  depth={0}
                />
              ) : (
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setCondTree({ type: 'group', op: 'AND', id: genId(), children: [] })}
                  style={{ color: C.brand, borderColor: C.brand, borderStyle: 'dashed' }}
                >
                  Add root condition
                </Button>
              )}
            </div>
          </div>

          {/* Output */}
          <div style={{ padding: '10px 12px', background: state === 1 ? C.state1Bg : state === 2 ? C.absentBg : '#FFFBEB', border: `1px solid ${state === 1 ? C.gospelBdr : state === 2 ? '#FECACA' : '#FDE68A'}`, borderRadius: 7 }}>
            <Text style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>Output</Text>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Type</Text>
                <Select value={rule.output?.type || 'BOOLEAN'} style={{ width: 110, fontSize: 13 }} onChange={val => onChange({ ...rule, output: { ...rule.output, type: val } })}>
                  <Option value="BOOLEAN">BOOLEAN</Option>
                </Select>
              </div>
              <div>
                <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Value</Text>
                <Select value={String(rule.output?.value ?? true)} style={{ width: 80, fontSize: 13 }} onChange={val => onChange({ ...rule, output: { ...rule.output, value: val === 'true' } })}>
                  <Option value="true">true</Option>
                  <Option value="false">false</Option>
                </Select>
              </div>
              {outcome && (
                <div>
                  <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Injects code</Text>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15, color: C.state1Color, background: '#fff', border: `1px solid ${C.gospelBdr}`, borderRadius: 4, padding: '3px 8px' }}>
                    {outcome.code}
                  </span>
                  <span style={{ marginLeft: 6, fontSize: 12, color: '#6B7280' }}>conf: {outcome.confidence}</span>
                </div>
              )}
              {isHallucin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <WarningOutlined style={{ color: C.absentColor, fontSize: 15 }} />
                  <Text style={{ fontSize: 13, color: C.absentColor, fontWeight: 600 }}>LLM Suppressed — no code injected</Text>
                </div>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <Text style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 3 }}>Reason</Text>
              <Input
                value={rule.output?.reason || ''}
                onChange={e => onChange({ ...rule, output: { ...rule.output, reason: e.target.value } })}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                placeholder="Audit reason string (prefix with 'hallucination_prevention:' for State 2)"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Vertical resize handle ── */}
      <div style={{ width: 6, flexShrink: 0, background: C.border, cursor: 'col-resize' }}
        onMouseEnter={e => e.currentTarget.style.background = C.brand + '55'}
        onMouseLeave={e => e.currentTarget.style.background = C.border}
      />

      {/* ── Right: YAML preview ── */}
      <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, background: '#F5F3FF', flexShrink: 0 }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>YAML Preview</Text>
          <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>Live — updates as you edit</Text>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          <YAMLHighlight code={yamlPreview} />
        </div>
      </div>
    </div>
  );
};

// ─── Outcomes Table ───────────────────────────────────────────────────────────
const OutcomesTable = ({ outcomes }) => {
  const data = useMemo(() =>
    Object.entries(outcomes || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([id, v]) => ({ key: id, rule_id: id, code: v.code, confidence: v.confidence })),
  [outcomes]);

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
          Outcomes — {data.length} entries
        </Text>
        <Text style={{ fontSize: 13, color: '#9CA3AF' }}>State 1 rules only · State 2 rules have no entry by design</Text>
      </div>
      <Table
        dataSource={data}
        size="small"
        pagination={false}
        rowKey="key"
        columns={[
          {
            title: 'Rule ID',
            dataIndex: 'rule_id',
            width: 320,
            render: v => <Text style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{v}</Text>,
          },
          {
            title: 'Injected Code',
            dataIndex: 'code',
            width: 140,
            render: v => (
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: C.state1Color, background: C.gospelBg, border: `1px solid ${C.gospelBdr}`, borderRadius: 4, padding: '2px 8px' }}>
                {v}
              </span>
            ),
          },
          {
            title: 'Confidence',
            dataIndex: 'confidence',
            width: 120,
            render: v => {
              const pct = Math.round(v * 100);
              const color = pct >= 88 ? C.state1Color : pct >= 80 ? '#D97706' : C.absentColor;
              return <Text style={{ fontSize: 14, fontWeight: 700, color }}>{pct}%</Text>;
            },
          },
        ]}
        style={{ fontSize: 13 }}
      />
    </div>
  );
};

// ─── Med Classes Table ────────────────────────────────────────────────────────
const MedClassTable = ({ medClasses }) => {
  const [search, setSearch] = useState('');

  const data = useMemo(() => {
    const entries = Object.entries(medClasses || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([drug, cls]) => ({ key: drug, drug, cls }));
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter(e => e.drug.includes(q) || e.cls.toLowerCase().includes(q));
  }, [medClasses, search]);

  const uniqueClasses = useMemo(() =>
    [...new Set(Object.entries(medClasses || {}).filter(([k]) => !k.startsWith('_')).map(([, v]) => v))].sort(),
  [medClasses]);

  const classColors = ['blue','purple','green','orange','red','cyan','magenta','gold','lime','geekblue'];
  const classColorMap = useMemo(() => {
    const map = {};
    uniqueClasses.forEach((cls, i) => { map[cls] = classColors[i % classColors.length]; });
    return map;
  }, [uniqueClasses]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
          placeholder="Search drug name or class…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 300, fontSize: 14 }}
          allowClear
        />
        <Text style={{ fontSize: 13, color: '#6B7280' }}>
          {data.length} entries · {uniqueClasses.length} classes
        </Text>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {uniqueClasses.map(cls => (
            <Tag
              key={cls}
              color={classColorMap[cls]}
              style={{ fontSize: 12, cursor: 'pointer' }}
              onClick={() => setSearch(cls)}
            >
              {cls}
            </Tag>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <Table
          dataSource={data}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `${t} entries` }}
          rowKey="key"
          columns={[
            {
              title: 'Drug Name',
              dataIndex: 'drug',
              width: 240,
              render: v => <Text style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>{v}</Text>,
              sorter: (a, b) => a.drug.localeCompare(b.drug),
            },
            {
              title: 'MED:: Class Token',
              dataIndex: 'cls',
              render: v => <Tag color={classColorMap[v]} style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>{v}</Tag>,
              filters: uniqueClasses.map(c => ({ text: c, value: c })),
              onFilter: (val, rec) => rec.cls === val,
              sorter: (a, b) => a.cls.localeCompare(b.cls),
            },
          ]}
        />
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const RuleEditorPage = () => {
  const [activeTab,      setActiveTab]      = useState('ner');
  const [corrSubTab,     setCorrSubTab]     = useState('rules');

  // data
  const [nerData,        setNerData]        = useState(null);
  const [corrData,       setCorrData]       = useState(null);
  const [outcomes,       setOutcomes]       = useState(null);
  const [medClasses,     setMedClasses]     = useState(null);
  const [loading,        setLoading]        = useState(true);

  // selection
  const [selNerRule,     setSelNerRule]     = useState(null);
  const [selCorrRule,    setSelCorrRule]    = useState(null);

  // panel widths (resizable)
  const [listW,          setListW]          = useState(240);

  const startDrag = useCallback((e, setter, getBase) => {
    e.preventDefault();
    const startX = e.clientX, base = getBase();
    const onMove = me => setter(Math.max(160, base + (me.clientX - startX)));
    const onUp   = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); document.body.style.cursor = ''; };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  // ── derived med class options ──
  const medClassOptions = useMemo(() =>
    [...new Set(Object.entries(medClasses || {}).filter(([k]) => !k.startsWith('_')).map(([, v]) => v))].sort(),
  [medClasses]);

  // ── load all files ──
  useEffect(() => {
    const loadFile = (path, parse = 'json') =>
      fetch(path).then(r => { if (!r.ok) throw new Error(path); return r.text(); })
        .then(t => parse === 'yaml' ? yaml.load(t) : JSON.parse(t));

    Promise.allSettled([
      loadFile('/rules/ner_code_corrections.yaml', 'yaml'),
      loadFile('/rules/corroboration_rules.yaml', 'yaml'),
      loadFile('/rules/corroboration_outcomes.json'),
      loadFile('/rules/corroboration_med_classes.json'),
    ]).then(([ner, corr, out, med]) => {
      if (ner.status === 'fulfilled') {
        setNerData(ner.value);
        const first = ner.value?.rules?.[0];
        if (first) setSelNerRule(first);
      }
      if (corr.status === 'fulfilled') {
        // attach parsed condition tree to each rule
        const rulesWithTree = (corr.value?.rules || []).map(r => ({
          ...r,
          _condTree: parseConditions(r.conditions),
        }));
        setCorrData({ ...corr.value, rules: rulesWithTree });
        if (rulesWithTree.length) setSelCorrRule(rulesWithTree[0]);
      }
      if (out.status === 'fulfilled') setOutcomes(out.value);
      if (med.status === 'fulfilled') setMedClasses(med.value);
      setLoading(false);
    });
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <Spin size="large" />
      <Text style={{ color: '#9CA3AF', fontSize: 14 }}>Loading rule files from /public/rules/…</Text>
    </div>
  );

  const corrRules = corrData?.rules || [];

  const tabStyle = (key) => ({
    padding: '0 20px',
    height: 38,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: activeTab === key ? 700 : 400,
    color: activeTab === key ? '#fff' : 'rgba(255,255,255,0.65)',
    borderBottom: activeTab === key ? '2px solid #fff' : '2px solid transparent',
    transition: 'all 0.15s',
    userSelect: 'none',
  });

  const subTabStyle = (key) => ({
    padding: '4px 14px',
    fontSize: 13,
    fontWeight: corrSubTab === key ? 700 : 400,
    cursor: 'pointer',
    color: corrSubTab === key ? C.brand : '#6B7280',
    background: corrSubTab === key ? C.brandLight : 'transparent',
    borderRadius: 6,
    border: corrSubTab === key ? `1px solid #DDD6FE` : '1px solid transparent',
    userSelect: 'none',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <div style={{
        height: C.HEADER_H, flexShrink: 0,
        background: `linear-gradient(90deg, ${C.brandDark} 0%, ${C.brand} 100%)`,
        display: 'flex', alignItems: 'stretch', padding: '0 0 0 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
      }}>
        {/* title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginRight: 24 }}>
          <MedicineBoxOutlined style={{ color: '#C4B5FD', fontSize: 18 }} />
          <div>
            <Text style={{ color: '#fff', fontWeight: 700, fontSize: 14, display: 'block', lineHeight: '17px' }}>Rule Editor</Text>
            <Text style={{ color: '#C4B5FD', fontSize: 12 }}>HCC Pipeline · NER + Corroboration</Text>
          </div>
        </div>

        {/* tabs */}
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={tabStyle('ner')} onClick={() => setActiveTab('ner')}>Code Corrections</div>
          <div style={tabStyle('corr')} onClick={() => setActiveTab('corr')}>Corroboration Rules</div>
        </div>

        {/* spacer + stats */}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingRight: 16 }}>
          {activeTab === 'ner' && nerData && (
            <>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#86EFAC', lineHeight: '20px' }}>{nerData.rules?.length || 0}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rules</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#FDE047', lineHeight: '20px' }}>{nerData.rules?.filter(r => r.scope === 'encounter').length || 0}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Encounter</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#C4B5FD', lineHeight: '20px' }}>{nerData.rules?.filter(r => r.scope === 'chart').length || 0}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Chart</div>
              </div>
            </>
          )}
          {activeTab === 'corr' && corrData && (
            <>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#86EFAC', lineHeight: '20px' }}>{corrRules.filter(r => !r.output?.reason?.startsWith('hallucination')).filter(r => outcomes?.[r.id]).length}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 1</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#FCA5A5', lineHeight: '20px' }}>{corrRules.filter(r => r.output?.reason?.startsWith('hallucination')).length}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 2</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#FDE047', lineHeight: '20px' }}>{corrRules.filter(r => !outcomes?.[r.id] && !r.output?.reason?.startsWith('hallucination')).length}</div>
                <div style={{ fontSize: 10, color: '#C4B5FD', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State 3</div>
              </div>
            </>
          )}
          <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.2)' }} />
          <Button
            icon={<SaveOutlined />}
            onClick={() => message.info('Save — will wire to FastAPI PUT /rules/{file}')}
            style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)', fontSize: 13 }}
          >
            Save
          </Button>
        </div>
      </div>

      {/* ══ BODY ════════════════════════════════════════════════════════════ */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', userSelect: 'none' }}>

        {/* ── NER TAB ──────────────────────────────────────────────────── */}
        {activeTab === 'ner' && (
          <>
            {/* P1 list */}
            <div style={{ width: listW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflow: 'hidden' }}>
              <PanelHeader
                label="Rules"
                sub={`${nerData?.namespace} · v${nerData?.version}`}
              />
              <NERRuleList
                rules={nerData?.rules || []}
                selectedId={selNerRule?.id}
                onSelect={r => setSelNerRule(r)}
              />
            </div>

            <ResizeHandle onDrag={e => startDrag(e, setListW, () => listW)} />

            {/* P2 form */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>
              <PanelHeader
                label={selNerRule?.id || 'Rule Editor'}
                sub={selNerRule ? `scope: ${selNerRule.scope} · priority: ${selNerRule.priority}` : 'Select a rule from the list'}
                right={selNerRule && (
                  <span style={{ fontSize: 13, padding: '2px 10px', borderRadius: 4, fontWeight: 700,
                    background: selNerRule.scope === 'encounter' ? C.encBg : '#F5F3FF',
                    color:      selNerRule.scope === 'encounter' ? C.encColor : C.chartColor,
                    border:     `1px solid ${selNerRule.scope === 'encounter' ? '#BAE6FD' : '#DDD6FE'}`,
                  }}>
                    {selNerRule.scope}
                  </span>
                )}
              />
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                {/* form */}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', userSelect: 'text' }}>
                  <NERRuleForm
                    rule={selNerRule}
                    onChange={r => setSelNerRule(r)}
                  />
                </div>
                {/* yaml preview */}
                <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <div style={{ padding: '7px 12px', borderBottom: `1px solid ${C.border}`, background: '#F5F3FF', flexShrink: 0 }}>
                    <Text style={{ fontSize: 12, fontWeight: 700, color: C.brand, letterSpacing: '0.08em', textTransform: 'uppercase' }}>YAML Preview</Text>
                    <Text style={{ fontSize: 12, color: '#9CA3AF', display: 'block' }}>Live · updates as you edit</Text>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: 12, userSelect: 'text' }}>
                    <YAMLHighlight code={selNerRule ? (() => { try { return yaml.dump({ id: selNerRule.id, scope: selNerRule.scope, priority: selNerRule.priority, trigger: selNerRule.trigger, correction: selNerRule.correction, reason: selNerRule.reason }, { lineWidth: 56, indent: 2 }); } catch { return ''; } })() : ''} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── CORROBORATION TAB ─────────────────────────────────────────── */}
        {activeTab === 'corr' && (
          <>
            {/* P1 list (only for rules sub-tab) */}
            {corrSubTab === 'rules' && (
              <>
                <div style={{ width: listW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflow: 'hidden' }}>
                  <PanelHeader
                    label="Rules"
                    sub={`${corrData?.namespace} · v${corrData?.version}`}
                  />
                  <CorrRuleList
                    rules={corrRules}
                    outcomes={outcomes}
                    selectedId={selCorrRule?.id}
                    onSelect={r => setSelCorrRule(r)}
                  />
                </div>
                <ResizeHandle onDrag={e => startDrag(e, setListW, () => listW)} />
              </>
            )}

            {/* P2 main area */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff' }}>

              {/* sub-tab bar */}
              <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.border}`, background: '#F9FAFB', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {[
                  { key: 'rules',      label: 'Rules',           count: corrRules.length },
                  { key: 'outcomes',   label: 'Outcomes',        count: Object.keys(outcomes || {}).filter(k => !k.startsWith('_')).length },
                  { key: 'medclasses', label: 'Med Classes',     count: Object.keys(medClasses || {}).filter(k => !k.startsWith('_')).length },
                ].map(({ key, label, count }) => (
                  <div key={key} onClick={() => setCorrSubTab(key)} style={subTabStyle(key)}>
                    {label}
                    <span style={{ marginLeft: 5, fontSize: 11, fontWeight: 700, padding: '0 5px', borderRadius: 9999, background: corrSubTab === key ? C.brand : '#E5E7EB', color: corrSubTab === key ? '#fff' : '#6B7280' }}>
                      {count}
                    </span>
                  </div>
                ))}
                {corrSubTab === 'rules' && selCorrRule && (
                  <>
                    <div style={{ flex: 1 }} />
                    <Text style={{ fontSize: 13, color: '#9CA3AF' }}>
                      {selCorrRule.id}
                    </Text>
                  </>
                )}
              </div>

              {/* sub-tab content */}
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden', userSelect: corrSubTab === 'rules' ? 'none' : 'text' }}>
                {corrSubTab === 'rules' && (
                  <CorrRuleEditor
                    rule={selCorrRule}
                    outcomes={outcomes}
                    onChange={r => {
                      setSelCorrRule(r);
                      setCorrData(prev => ({
                        ...prev,
                        rules: prev.rules.map(x => x.id === r.id ? r : x),
                      }));
                    }}
                    medClasses={medClassOptions}
                  />
                )}
                {corrSubTab === 'outcomes' && <OutcomesTable outcomes={outcomes} />}
                {corrSubTab === 'medclasses' && <MedClassTable medClasses={medClasses} />}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RuleEditorPage;